import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useTheme } from '../../ThemeContext';
import { Link } from 'react-router-dom';

// Icons
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MovieFilterRoundedIcon from '@mui/icons-material/MovieFilterRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import StarsRoundedIcon from '@mui/icons-material/StarsRounded';

// Store Icons & Assets
import { GooglePlayIcon, AppleIcon } from './StoreIcons';
import {
  PLAY_STORE_URL,
  APP_STORE_URL,
  EDITOR_PLAY_STORE_URL,
  AI_PLAY_STORE_URL,
} from '../../constants/links';
import appLogo from '../../assets/Filmhook_Apps_Logoonly.png';
import editorLogo from '../../assets/editor/editor_icon.png';
import aiLogo from '../../assets/filmhook_ai_icon.png';
import SS1 from '../../assets/SS1.png';
import SS2 from '../../assets/SS2.png';
import SS3 from '../../assets/SS3.png';
import aiShot1 from '../../assets/ai_shot_1.png';
import aiShot2 from '../../assets/ai_shot_2.png';
import editorShot1 from '../../assets/editor/editor_shot_1.png';
import editorShot2 from '../../assets/editor/editor_shot_2.png';

// App Editions Definition with Real Screenshot Assets
export const APPS_DATA = [
  {
    id: 'filmhook-android',
    name: 'Google Play',
    sub: 'Filmhook App',
    title: 'Filmhook App',
    edition: 'Android Edition',
    store: 'Google Play',
    storeUrl: PLAY_STORE_URL,
    icon: <GooglePlayIcon size={20} />,
    logo: appLogo,
    color: '#07a4f5',
    activeGradient: 'linear-gradient(135deg, #07a4f5 0%, #0284c7 100%)',
    badge: 'FLAGSHIP APP',
    backImage: SS2,
    frontImage: SS1,
    frontBadge: 'Verified Castings',
    frontSubtitle: 'Social Media & Casting for Cinema Creators',
  },
  {
    id: 'filmhook-ios',
    name: 'App Store',
    sub: 'iOS App',
    title: 'Filmhook iOS',
    edition: 'Apple iOS Edition',
    store: 'App Store',
    storeUrl: APP_STORE_URL,
    icon: <AppleIcon size={20} />,
    logo: appLogo,
    color: '#0284c7',
    activeGradient: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
    badge: 'APPLE iOS',
    backImage: SS3,
    frontImage: SS2,
    frontBadge: 'Audition Reels',
    frontSubtitle: 'Cinema Network & Casting Call Auditions',
  },
  {
    id: 'filmhook-editor',
    name: 'Filmhook Editor',
    sub: 'Play Store',
    title: 'Filmhook Editor',
    edition: 'Android Edition',
    store: 'Google Play',
    storeUrl: EDITOR_PLAY_STORE_URL,
    icon: <GooglePlayIcon size={20} />,
    logo: editorLogo,
    color: '#a855f7',
    activeGradient: 'linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)',
    badge: '4K NLE TIMELINE',
    backImage: editorShot2,
    frontImage: editorShot1,
    frontBadge: '4K Multi-Track NLE',
    frontSubtitle: 'Multi-Track 4K NLE Video Editor & LUTs',
  },
  {
    id: 'filmhook-ai',
    name: 'Filmhook AI',
    sub: 'Play Store',
    title: 'Filmhook AI',
    edition: 'Android Edition',
    store: 'Google Play',
    storeUrl: AI_PLAY_STORE_URL,
    icon: <GooglePlayIcon size={20} />,
    logo: aiLogo,
    color: '#0095F6',
    activeGradient: 'linear-gradient(135deg, #0095F6 0%, #0077E6 100%)',
    badge: 'GEN AI ASSISTANT',
    backImage: aiShot2,
    frontImage: aiShot1,
    frontBadge: 'Script-to-Storyboard',
    frontSubtitle: 'Your AI Assistant for the Media Industry',
  },
];

const TRUST_BADGES = [
  {
    icon: <VerifiedRoundedIcon sx={{ fontSize: 20, color: '#0095F6' }} />,
    title: 'Verified Apps',
    desc: 'Official & secure downloads',
  },
  {
    icon: <UpdateRoundedIcon sx={{ fontSize: 20, color: '#0095F6' }} />,
    title: 'Latest Version',
    desc: 'Always up to date',
  },
  {
    icon: <DevicesRoundedIcon sx={{ fontSize: 20, color: '#0095F6' }} />,
    title: 'Multi-Platform',
    desc: 'Android, iOS & more',
  },
  {
    icon: <SecurityRoundedIcon sx={{ fontSize: 20, color: '#0095F6' }} />,
    title: 'Safe & Trusted',
    desc: 'Secured by Filmhook',
  },
];

export default function DownloadAppShowcase() {
  const { isDarkMode } = useTheme();
  const [selectedAppId, setSelectedAppId] = useState('filmhook-ai');

  const selectedApp = APPS_DATA.find((a) => a.id === selectedAppId) || APPS_DATA[3];

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: isDarkMode
          ? 'linear-gradient(180deg, #070e1c 0%, #040812 100%)'
          : 'linear-gradient(180deg, #eef5ff 0%, #e2edfd 50%, #d8e6fa 100%)',
        pt: { xs: 8, md: 10 },
        pb: { xs: 5, md: 6 },
        borderTop: isDarkMode
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(7, 164, 245, 0.18)',
        transition: 'background 0.4s ease',
      }}
    >
      {/* Soft Ambient Cinematic Glow Waves in Background */}
      <Box
        sx={{
          position: 'absolute',
          top: '-15%',
          right: '-5%',
          width: { xs: '350px', md: '650px' },
          height: { xs: '350px', md: '650px' },
          background: isDarkMode
            ? 'radial-gradient(circle, rgba(7, 164, 245, 0.15) 0%, rgba(59, 130, 246, 0.05) 50%, transparent 70%)'
            : 'radial-gradient(circle, rgba(147, 197, 253, 0.6) 0%, rgba(191, 219, 254, 0.3) 50%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-5%',
          width: { xs: '300px', md: '500px' },
          height: { xs: '300px', md: '500px' },
          background: isDarkMode
            ? 'radial-gradient(circle, rgba(147, 51, 234, 0.12) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(216, 180, 254, 0.4) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Top Header Pill & Title */}
        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <Chip
            icon={<DownloadRoundedIcon sx={{ fontSize: '15px !important', color: '#0095F6 !important' }} />}
            label="Download Center"
            size="small"
            sx={{
              fontWeight: 800,
              fontSize: '0.74rem',
              bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.12)' : 'rgba(255, 255, 255, 0.85)',
              color: '#0095F6',
              border: '1px solid rgba(0, 149, 246, 0.3)',
              mb: 1.5,
              px: 0.8,
              boxShadow: '0 2px 8px rgba(0, 149, 246, 0.12)',
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2rem', sm: '2.4rem', md: '2.8rem' },
              color: isDarkMode ? '#ffffff' : '#0a192f',
              letterSpacing: '-0.025em',
              mb: 1,
            }}
          >
            Get the Filmhook Apps
          </Typography>
          <Typography
            sx={{
              color: isDarkMode ? '#94a3b8' : '#475569',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              maxWidth: '680px',
              lineHeight: 1.5,
            }}
          >
            Access the complete Filmhook ecosystem — AI, Editor, and industry tools for creators, production teams and studios.
          </Typography>
        </Box>

        {/* Main 3-Column Showcase Section */}
        <Grid container spacing={3.5} alignItems="center">
          {/* Left Column: Vertical Store Selector Tabs */}
          <Grid size={{ xs: 12, md: 3.5, lg: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.6 }}>
              {APPS_DATA.map((app) => {
                const isSelected = selectedAppId === app.id;
                return (
                  <Box
                    key={app.id}
                    component={motion.div}
                    whileHover={{ x: isSelected ? 0 : 4, scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedAppId(app.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2.2,
                      py: 1.6,
                      borderRadius: '16px',
                      cursor: 'pointer',
                      background: isSelected
                        ? app.activeGradient
                        : isDarkMode
                        ? 'rgba(255, 255, 255, 0.04)'
                        : 'rgba(255, 255, 255, 0.85)',
                      color: isSelected ? '#ffffff' : isDarkMode ? '#ffffff' : '#0f172a',
                      border: isSelected
                        ? '1.5px solid rgba(255, 255, 255, 0.3)'
                        : isDarkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(255, 255, 255, 0.9)',
                      boxShadow: isSelected
                        ? '0 12px 30px rgba(0, 149, 246, 0.35), 0 0 15px rgba(0, 149, 246, 0.2)'
                        : '0 4px 14px rgba(0, 0, 0, 0.03)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.25s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.6 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isSelected ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                        }}
                      >
                        {app.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', lineHeight: 1.2 }}>
                          {app.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.72rem',
                            opacity: isSelected ? 0.9 : 0.65,
                            mt: 0.2,
                          }}
                        >
                          {app.sub}
                        </Typography>
                      </Box>
                    </Box>

                    <ChevronRightRoundedIcon
                      sx={{
                        fontSize: 20,
                        opacity: isSelected ? 1 : 0.45,
                        transform: isSelected ? 'translateX(2px)' : 'none',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Grid>

          {/* Middle Column: The Large Rounded White Scanner Showcase Card */}
          <Grid size={{ xs: 12, md: 8.5, lg: 5.5 }}>
            <Box
              component={motion.div}
              layout
              transition={{ duration: 0.3 }}
              sx={{
                background: isDarkMode
                  ? 'rgba(15, 23, 42, 0.85)'
                  : 'rgba(255, 255, 255, 0.92)',
                borderRadius: '28px',
                border: isDarkMode
                  ? '1.5px solid rgba(255, 255, 255, 0.12)'
                  : '1.5px solid #ffffff',
                boxShadow: isDarkMode
                  ? '0 24px 60px rgba(0, 0, 0, 0.6), 0 0 35px rgba(0, 149, 246, 0.1)'
                  : '0 20px 50px rgba(15, 23, 42, 0.08), 0 2px 10px rgba(0, 149, 246, 0.04)',
                backdropFilter: 'blur(20px)',
                p: { xs: 2.5, sm: 3.5 },
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Card Top Row: App Logo, Verified Badge, Store Button & External Link */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
                  <Box
                    component="img"
                    src={selectedApp.logo}
                    alt={selectedApp.title}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      objectFit: 'contain',
                      bgcolor: '#fff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      p: 0.4,
                    }}
                  />
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: isDarkMode ? '#fff' : '#0f172a' }}>
                        {selectedApp.title}
                      </Typography>
                      <VerifiedRoundedIcon sx={{ fontSize: 18, color: '#0095F6' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.78rem', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                      {selectedApp.edition}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    component="a"
                    href={selectedApp.storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={selectedApp.icon}
                    sx={{
                      bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9',
                      color: isDarkMode ? '#fff' : '#0f172a',
                      fontWeight: 800,
                      fontSize: '0.78rem',
                      textTransform: 'none',
                      borderRadius: '12px',
                      px: 1.6,
                      py: 0.7,
                      border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e2e8f0',
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0',
                      },
                    }}
                  >
                    {selectedApp.store}
                  </Button>
                  <IconButton
                    component="a"
                    href={selectedApp.storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      '&:hover': { color: '#0095F6' },
                    }}
                  >
                    <OpenInNewRoundedIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Middle Row: Viewfinder QR Code on Left + Scan & Install Instructions on Right */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 2.5, sm: 3.5 },
                  mb: 3.5,
                }}
              >
                {/* Viewfinder Target Container */}
                <Box
                  sx={{
                    position: 'relative',
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* Cyan Four Corner Brackets (Exact Match to screenshot) */}
                  <Box sx={{ position: 'absolute', top: 0, left: 0, width: 22, height: 22, borderTop: '3.5px solid #0095F6', borderLeft: '3.5px solid #0095F6', borderRadius: '6px 0 0 0' }} />
                  <Box sx={{ position: 'absolute', top: 0, right: 0, width: 22, height: 22, borderTop: '3.5px solid #0095F6', borderRight: '3.5px solid #0095F6', borderRadius: '0 6px 0 0' }} />
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: 22, height: 22, borderBottom: '3.5px solid #0095F6', borderLeft: '3.5px solid #0095F6', borderRadius: '0 0 0 6px' }} />
                  <Box sx={{ position: 'absolute', bottom: 0, right: 0, width: 22, height: 22, borderBottom: '3.5px solid #0095F6', borderRight: '3.5px solid #0095F6', borderRadius: '0 0 6px 0' }} />

                  {/* Laser Scan Beam Animation */}
                  <Box
                    component={motion.div}
                    animate={{ y: [-50, 50, -50] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                    sx={{
                      position: 'absolute',
                      left: 10,
                      right: 10,
                      height: '2px',
                      background: 'linear-gradient(90deg, transparent, #0095F6, transparent)',
                      boxShadow: '0 0 10px #0095F6',
                      zIndex: 3,
                      pointerEvents: 'none',
                    }}
                  />

                  {/* QR Box with centered App Logo */}
                  <Box
                    sx={{
                      p: 1.2,
                      bgcolor: '#ffffff',
                      borderRadius: '16px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <QRCodeSVG
                      value={selectedApp.storeUrl}
                      size={135}
                      level="H"
                      includeMargin={false}
                      fgColor="#0a192f"
                      bgColor="#ffffff"
                    />
                    {/* Centered App Logo Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        bgcolor: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                        p: 0.3,
                      }}
                    >
                      <Box
                        component="img"
                        src={selectedApp.logo}
                        alt="icon"
                        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Instructions & Scanner Pill */}
                <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 900,
                      fontSize: '1.25rem',
                      color: isDarkMode ? '#ffffff' : '#0f172a',
                      mb: 0.8,
                    }}
                  >
                    Scan & Install
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.84rem',
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      lineHeight: 1.55,
                      mb: 2,
                    }}
                  >
                    Scan the QR code with your phone camera to download {selectedApp.title} from {selectedApp.store}.
                  </Typography>

                  {/* Pill button matching screenshot */}
                  <Box
                    component="a"
                    href={selectedApp.storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.8,
                      px: 1.6,
                      py: 0.6,
                      borderRadius: '20px',
                      bgcolor: isDarkMode ? 'rgba(0, 149, 246, 0.15)' : '#e0f2fe',
                      color: '#0284c7',
                      border: '1px solid #bae6fd',
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                      },
                    }}
                  >
                    <CameraAltRoundedIcon sx={{ fontSize: 16 }} />
                    Scan with phone camera
                    <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                  </Box>
                </Box>
              </Box>

              {/* Bottom "OTHER FILMHOOK APPS" dock */}
              <Box sx={{ pt: 2, borderTop: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #f1f5f9' }}>
                <Typography
                  sx={{
                    fontSize: '0.66rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    color: isDarkMode ? '#64748b' : '#94a3b8',
                    textTransform: 'uppercase',
                    mb: 1.2,
                  }}
                >
                  OTHER FILMHOOK APPS
                </Typography>

                <Grid container spacing={1.2}>
                  {APPS_DATA.map((item) => {
                    const isSelected = selectedAppId === item.id;
                    return (
                      <Grid size={{ xs: 6, sm: 3 }} key={item.id}>
                        <Box
                          component={motion.div}
                          whileHover={{ y: -3 }}
                          whileTap={{ scale: 0.96 }}
                          onClick={() => setSelectedAppId(item.id)}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1.2,
                            borderRadius: '16px',
                            cursor: 'pointer',
                            bgcolor: isSelected
                              ? isDarkMode
                                ? 'rgba(0, 149, 246, 0.15)'
                                : '#f0f9ff'
                              : isDarkMode
                              ? 'rgba(255, 255, 255, 0.03)'
                              : '#ffffff',
                            border: isSelected
                              ? '1.5px solid #0095F6'
                              : isDarkMode
                              ? '1px solid rgba(255, 255, 255, 0.08)'
                              : '1px solid #e2e8f0',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <Box
                            component="img"
                            src={item.logo}
                            alt={item.title}
                            sx={{ width: 28, height: 28, objectFit: 'contain', mb: 0.5 }}
                          />
                          <Typography
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 800,
                              color: isSelected ? '#0095F6' : isDarkMode ? '#fff' : '#0f172a',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography sx={{ fontSize: '0.62rem', color: isDarkMode ? '#64748b' : '#94a3b8' }}>
                            {item.store}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          </Grid>

          {/* Right Column: 3D Dual Real-Device Phone Mockup with Real App Screens & Floating Animation */}
          <Grid size={{ xs: 12, lg: 3.5 }}>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: { xs: 400, sm: 440, lg: 470 },
                perspective: '1200px',
              }}
            >
              {/* Back Floating Phone with Genuine App Screenshot */}
              <Box
                component={motion.div}
                animate={{
                  y: [0, -10, 0],
                  rotateY: [-12, -8, -12],
                }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                sx={{
                  position: 'absolute',
                  left: { xs: '2%', sm: '8%', lg: '0%' },
                  width: { xs: 185, sm: 205, md: 220 },
                  height: { xs: 380, sm: 420, md: 450 },
                  borderRadius: '38px',
                  border: '6px solid #1e293b',
                  boxShadow: '0 25px 60px rgba(0, 0, 0, 0.25), 0 0 25px rgba(0, 149, 246, 0.15)',
                  overflow: 'hidden',
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: '#0a0f1d',
                  background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
                }}
              >
                {/* Dynamic Island / Speaker Pill */}
                <Box
                  sx={{
                    width: 70,
                    height: 5,
                    bgcolor: '#000000',
                    borderRadius: '10px',
                    mx: 'auto',
                    mt: 1,
                    mb: 0.8,
                    zIndex: 10,
                  }}
                />

                {/* Real App Screenshot Image */}
                <Box
                  sx={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AnimatePresence mode="wait">
                    <Box
                      key={selectedApp.id + '-back'}
                      component={motion.img}
                      src={selectedApp.backImage}
                      alt="App Screen"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.05 }}
                      transition={{ duration: 0.4 }}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </AnimatePresence>

                  {/* Gradient Scrim */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, transparent 40%, rgba(0,0,0,0.5) 100%)',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>

                {/* Bottom Home Indicator */}
                <Box sx={{ py: 0.8, display: 'flex', justifyContent: 'center', zIndex: 10, bgcolor: 'rgba(0,0,0,0.4)' }}>
                  <Box sx={{ width: 65, height: 3.5, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: '3px' }} />
                </Box>
              </Box>

              {/* Front Overlapping Phone (Real Active Screenshot with Glowing Floating Shadow) */}
              <Box
                component={motion.div}
                animate={{
                  y: [0, -14, 0],
                  rotateY: [10, 6, 10],
                }}
                transition={{ duration: 5.2, repeat: Infinity, ease: 'easeInOut' }}
                sx={{
                  position: 'relative',
                  left: { xs: '20%', sm: '26%', lg: '28%' },
                  width: { xs: 195, sm: 215, md: 230 },
                  height: { xs: 400, sm: 440, md: 470 },
                  borderRadius: '40px',
                  border: '6px solid #0f172a',
                  boxShadow: `0 35px 80px rgba(0, 0, 0, 0.45), 0 0 40px ${selectedApp.color}40`,
                  overflow: 'hidden',
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: '#0a0f1d',
                  background: 'linear-gradient(180deg, #091326 0%, #030814 100%)',
                }}
              >
                {/* Dynamic Island */}
                <Box
                  sx={{
                    width: 75,
                    height: 5.5,
                    bgcolor: '#000000',
                    borderRadius: '12px',
                    mx: 'auto',
                    mt: 1,
                    mb: 0.8,
                    zIndex: 10,
                  }}
                />

                {/* Front Phone Screenshot Container */}
                <Box
                  sx={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <AnimatePresence mode="wait">
                    <Box
                      key={selectedApp.id + '-front'}
                      component={motion.div}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.35 }}
                      sx={{ width: '100%', height: '100%', position: 'relative' }}
                    >
                      <Box
                        component="img"
                        src={selectedApp.frontImage}
                        alt={selectedApp.title}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />

                      {/* Top Glass Badge Pill on Phone Screen */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          left: 10,
                          right: 10,
                          p: 0.8,
                          borderRadius: '12px',
                          background: 'rgba(15, 23, 42, 0.75)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                          <Box
                            component="img"
                            src={selectedApp.logo}
                            alt="app"
                            sx={{ width: 16, height: 16, borderRadius: '4px' }}
                          />
                          <Typography sx={{ color: '#fff', fontSize: '0.68rem', fontWeight: 800 }}>
                            {selectedApp.title}
                          </Typography>
                        </Box>

                        <Chip
                          label={selectedApp.frontBadge}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.58rem',
                            fontWeight: 800,
                            bgcolor: `${selectedApp.color}35`,
                            color: '#ffffff',
                            border: `1px solid ${selectedApp.color}`,
                          }}
                        />
                      </Box>

                      {/* Bottom Floating Glass Card with CTA */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 12,
                          left: 10,
                          right: 10,
                          p: 1.2,
                          borderRadius: '14px',
                          background: 'rgba(15, 23, 42, 0.85)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(255, 255, 255, 0.18)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                        }}
                      >
                        <Typography sx={{ color: '#ffffff', fontSize: '0.72rem', fontWeight: 800, lineHeight: 1.2 }}>
                          {selectedApp.frontSubtitle}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.8 }}>
                          <Typography sx={{ color: '#38bdf8', fontSize: '0.62rem', fontWeight: 700 }}>
                            ★★★★★ 4.9 Rating
                          </Typography>
                          <Typography
                            sx={{
                              color: '#fff',
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              bgcolor: selectedApp.color,
                              px: 0.9,
                              py: 0.2,
                              borderRadius: '6px',
                            }}
                          >
                            Install Now →
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </AnimatePresence>
                </Box>

                {/* Bottom Home Indicator */}
                <Box sx={{ py: 0.8, display: 'flex', justifyContent: 'center', zIndex: 10, bgcolor: 'rgba(0,0,0,0.5)' }}>
                  <Box sx={{ width: 75, height: 4, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: '3px' }} />
                </Box>
              </Box>

              {/* Decorative 3D Slate Clapperboard Graphic Floating Top-Right */}
              <Box
                component={motion.div}
                animate={{
                  y: [0, -10, 0],
                  rotate: [15, 22, 15],
                }}
                transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                sx={{
                  position: 'absolute',
                  top: '-8%',
                  right: { xs: '-8%', lg: '-6%' },
                  width: { xs: 80, md: 105 },
                  height: { xs: 80, md: 105 },
                  zIndex: 0,
                  opacity: 0.85,
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 15px 30px rgba(0, 149, 246, 0.25))',
                }}
              >
                <MovieFilterRoundedIcon sx={{ fontSize: { xs: 80, md: 105 }, color: '#38bdf8' }} />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Trust & Compliance Badges Strip */}
        <Box
          sx={{
            mt: 6,
            pt: 3.5,
            borderTop: isDarkMode
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 149, 246, 0.15)',
          }}
        >
          <Grid container spacing={3} justifyContent="space-between">
            {TRUST_BADGES.map((b, i) => (
              <Grid size={{ xs: 6, sm: 6, md: 3 }} key={i}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      bgcolor: isDarkMode ? 'rgba(0, 149, 246, 0.12)' : 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(0, 149, 246, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0, 149, 246, 0.08)',
                    }}
                  >
                    {b.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', color: isDarkMode ? '#fff' : '#0f172a' }}>
                      {b.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                      {b.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Global Footer Legal Links & Copyright */}
        <Box
          sx={{
            mt: 5,
            pt: 3,
            borderTop: isDarkMode
              ? '1px solid rgba(255, 255, 255, 0.06)'
              : '1px solid rgba(0, 149, 246, 0.1)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Company Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1.15rem', color: isDarkMode ? '#fff' : '#0095F6' }}>
              Filmhook®
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
              Filmhook Mediaapps Pvt Ltd.
            </Typography>
          </Box>

          {/* Legal Navigation Links */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
            <Link to="/termsAndCondition" style={{ textDecoration: 'none' }}>
              <Typography sx={{ fontSize: '0.8rem', color: isDarkMode ? '#94a3b8' : '#475569', '&:hover': { color: '#0095F6' } }}>
                Terms & Conditions
              </Typography>
            </Link>
            <Typography sx={{ color: isDarkMode ? '#475569' : '#cbd5e1' }}>|</Typography>
            <Link to="/privacyPolicyAll" style={{ textDecoration: 'none' }}>
              <Typography sx={{ fontSize: '0.8rem', color: isDarkMode ? '#94a3b8' : '#475569', '&:hover': { color: '#0095F6' } }}>
                Privacy Policy
              </Typography>
            </Link>
            <Typography sx={{ color: isDarkMode ? '#475569' : '#cbd5e1' }}>|</Typography>
            <Link to="/data-safety" style={{ textDecoration: 'none' }}>
              <Typography sx={{ fontSize: '0.8rem', color: isDarkMode ? '#94a3b8' : '#475569', '&:hover': { color: '#0095F6' } }}>
                Data Safety
              </Typography>
            </Link>
            <Typography sx={{ color: isDarkMode ? '#475569' : '#cbd5e1' }}>|</Typography>
            <Link to="/account-deletion" style={{ textDecoration: 'none' }}>
              <Typography sx={{ fontSize: '0.8rem', color: isDarkMode ? '#94a3b8' : '#475569', '&:hover': { color: '#0095F6' } }}>
                Account Deletion
              </Typography>
            </Link>
          </Box>

          {/* Email Support */}
          <Box
            component="a"
            href="mailto:support@filmhookapps.com"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              color: isDarkMode ? '#94a3b8' : '#475569',
              fontSize: '0.8rem',
              textDecoration: 'none',
              '&:hover': { color: '#0095F6' },
            }}
          >
            ✉ support@filmhookapps.com
          </Box>
        </Box>

        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '0.74rem',
            color: isDarkMode ? '#475569' : '#94a3b8',
            mt: 3,
          }}
        >
          © 2026 Filmhook Mediaapps Pvt Ltd. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}
